import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../components/Layout';
import Drawer from '../components/Drawer';
import InfoTip from '../components/InfoTip';
import Modal from '../components/Modal';
import Spinner from '../components/Spinner';
import { usePersonLabels, useVisibleActions } from '../hooks/useScopedData';
import {
  downloadCsv,
  downloadXlsx,
  stamp,
  yn,
  type Column,
} from '../lib/exporters';
import { downloadReportWorkbook } from '../lib/reports/xlsxDownload';
import { buildUnitExport } from '../lib/reports/unitExport';
import { EmailLink, PhoneLink } from '../components/ContactLink';
import { commissionerCoverage, commissionerLines, coverageByUnit, linksByUnit, ORIGIN_LABELS } from '../lib/unitCommissioners';
import {
  useRecentUnitLeaderSnapshots,
  useUnitAdvancementHistory,
  useUnitImports,
  useUnitLeaderSnapshots,
  useVisibleSyt,
  useVisibleUnitLeaders,
  useVisibleUnitCommissioners,
  useVisibleUnits,
} from '../hooks/useUnits';
import {
  advForUnit,
  advForUnitProgram,
  advProgram,
  advancementState,
  daysUntil,
  evaluateHazardousWeather,
  HAZARDOUS_WEATHER_STATUS_LABELS,
  HAZARDOUS_WEATHER_TONE,
  leaderMatchesFilter,
  LEADER_FILTER_LABELS,
  leadershipTone,
  rechartCountdown,
  unitMatchesFilter,
  UNIT_FILTER_LABELS,
  UNIT_TYPE_ORDER,
  UNIT_TYPE_PLURAL,
  YPT_TONE,
  type AdvState,
  type HazardousWeatherEvaluation,
  type LeaderFilter,
  type Tone,
  type UnitFilter,
} from '../lib/unitHealth';
import {
  LEADER_SCOPE_LABELS,
  recName,
  type LeaderScope,
  type NamedRecord,
  UNIT_REPORT_LABELS,
  type Unit,
  type UnitType,
  type UnitAdvancementSnapshot,
  type UnitCommissionerAssignment,
  type UnitLeader,
  type UnitReportType,
  type UnitSyt,
  type UserProfile,
  type Action,
  type UnitLeaderSnapshot,
} from '../types';
import {
  attentionCategoriesFor,
  ATTENTION_CATEGORY_LABELS,
  computeUnitLeadershipSustainability,
  describeTenure,
  type AttentionCategory,
  type UnitLeadershipSustainability,
} from '../lib/leadershipSustainability';


function ToneDot({ tone }: { tone: Tone }) {
  return <span className={`uh-dot uh-${tone}`} aria-hidden />;
}

function sytTooltip(u: Unit): string {
  if (!u.key3) return '';
  const flagged = u.key3.people.filter((p) => p.ypt === 'expired' || p.ypt === 'expiring');
  if (flagged.length === 0) return "All Key 3 leaders' Safeguarding Youth Training is current.";
  return `Safeguarding Youth Training needs attention: ${flagged
    .map((p) => `${p.position} (${p.yptRaw})`)
    .join(', ')}.`;
}

/** Course-level training codes we can name — from BSA's "Position Trained
 *  Requirements" (May 2022). The many SCO_NNN e-learning module codes are
 *  deliberately NOT listed: they're pieces of these courses and naming each
 *  one just makes a longer wall of text. `describeIncomplete()` names the
 *  courses it recognizes and counts the rest. */
const COURSE_NAMES: Record<string, string> = {
  Y01: 'Youth Protection Training',
  SCO_800: 'Hazardous Weather Training',
  SCO_530: 'Journey to Excellence',
  SCO_1264: 'Membership Ethics',
  // Cub Scouting
  C40: 'Cubmaster Position-Specific Training',
  C42: 'Den Leader Position-Specific Training',
  C60: 'Pack Committee Challenge',
  // Scouts BSA
  S24: 'Scoutmaster Position-Specific Training',
  S11: 'Introduction to Outdoor Leader Skills (IOLS)',
  WS10: 'Troop Committee Challenge',
  D76: 'Merit Badge Counselor Orientation',
  // Venturing
  P21: 'Venturing Advisor Position-Specific Training',
  WS12: 'Crew Committee Position-Specific Training',
  // District / council / unit-serving
  D72: 'Training the Chartered Organization Representative',
  D20: 'Unit Commissioner Basic Training',
  D19: 'Roundtable Commissioner Basic Training',
  D18: 'District Commissioner Basic Training',
  D17: 'Council Commissioner Basic Training',
  D61: 'District Committee Chair Training',
  D62: 'District Vice Chair Training',
  D75: 'District Committee Member Training',
  H91: 'Council Board Member Training',
  H109: 'Council President / Vice President Training',
};

/** Turn a position's incomplete-course code list into a readable line:
 *  name the courses we recognize, count the rest. */
function describeIncomplete(codes: string[]): string {
  if (codes.length === 0) return 'Not recorded as position-trained';
  const named = [...new Set(codes.map((c) => COURSE_NAMES[c]).filter(Boolean))];
  const unnamed = codes.filter((c) => !COURSE_NAMES[c]).length;
  if (named.length === 0) {
    return `${codes.length} training module${codes.length === 1 ? '' : 's'} outstanding`;
  }
  const more = unnamed > 0 ? ` · +${unnamed} more module${unnamed === 1 ? '' : 's'}` : '';
  return `${named.join(', ')}${more}`;
}

interface SytState {
  tone: Tone;
  label: string;
  days: number | null;
}
function sytState(syt: UnitSyt | undefined): SytState | null {
  if (!syt) return null;
  const days = syt.expiresOn ? daysUntil(syt.expiresOn) : null;
  if (!syt.isCurrent) return { tone: 'red', label: 'Expired', days };
  if (days != null && days < 30) return { tone: 'red', label: 'Current', days };
  if (days != null && days < 90) return { tone: 'amber', label: 'Current', days };
  return { tone: 'ok', label: 'Current', days };
}

function leadershipTooltip(lt: { missing: string[] }): string {
  if (lt.missing.length === 0) {
    return 'All three registered leaders are in place: Committee Chair, unit leader (Cubmaster / Scoutmaster / Advisor / Skipper), and Chartered Organization Rep.';
  }
  return `No registered ${lt.missing.join(' and no ')} for this unit.`;
}

function advancementTooltip(u: Unit, as: AdvState): string {
  const year = as.snap.programYear;
  const { ranksYtd, youth } = as.snap;
  if (as.label === 'Stalled') {
    return `No rank advancements recorded in ${year} so far, with ${youth} registered youth.`;
  }
  if (as.label === 'Behind') {
    return `${ranksYtd} rank advancement${ranksYtd === 1 ? '' : 's'} in ${year} so far vs. ${youth} registered youth — under half; a pack would normally see roughly one rank per youth per year.`;
  }
  return `${ranksYtd} rank advancement${ranksYtd === 1 ? '' : 's'} recorded in ${year} so far (${youth} registered youth).${u.type === 'pack' ? '' : ' Troop pace is uneven month to month, so this only flags a unit with none at all.'}`;
}

const RANK_LABEL: Record<string, string> = {
  light: 'AOL',
  secondclass: 'Second Class',
  firstclass: 'First Class',
};
const rankLabel = (k: string) => RANK_LABEL[k] ?? k.charAt(0).toUpperCase() + k.slice(1);

// ---- export rows --------------------------------------------------

interface UnitExportRow {
  district: string;
  unit: string;
  type: string;
  youth: number;
  charterExpires: string;
  daysToRecharter: number | '';
  recharterStatus: string;
  committeeChair: string;
  unitLeader: string;
  charterOrgRep: string;
  leadershipGap: string;
  key3Syt: string;
  trained: string;
  advancement: string;
  scoutsBsaRanksYtd: number | '';
  cubRanksYtd: number | '';
  eaglesYtd: number | '';
}

const UNIT_EXPORT_COLUMNS: Column<UnitExportRow>[] = [
  { label: 'District', get: (r) => r.district },
  { label: 'Unit', get: (r) => r.unit },
  { label: 'Type', get: (r) => r.type },
  { label: 'Youth', get: (r) => r.youth },
  { label: 'Charter expires', get: (r) => r.charterExpires },
  { label: 'Days to recharter', get: (r) => r.daysToRecharter },
  { label: 'Recharter status', get: (r) => r.recharterStatus },
  { label: 'Committee Chair', get: (r) => r.committeeChair },
  { label: 'Unit leader', get: (r) => r.unitLeader },
  { label: 'Charter Org Rep', get: (r) => r.charterOrgRep },
  { label: 'Leadership gap', get: (r) => r.leadershipGap },
  { label: 'Key 3 SYT', get: (r) => r.key3Syt },
  { label: 'Trained in position', get: (r) => r.trained },
  { label: 'Advancement pace', get: (r) => r.advancement },
  { label: 'Scouts BSA ranks YTD', get: (r) => r.scoutsBsaRanksYtd },
  { label: 'Cub ranks YTD', get: (r) => r.cubRanksYtd },
  { label: 'Eagles YTD', get: (r) => r.eaglesYtd },
];

interface LeaderExportRow {
  name: string;
  email: string;
  phone: string;
  districts: string;
  scope: string;
  where: string;
  positions: string;
  untrainedPositions: string;
  incompleteCourses: string;
  anyUntrained: string;
  sytCurrent: string;
  sytExpires: string;
  registrationExpires: string;
  hazardousWeatherStatus: string;
  hazardousWeatherCompleted: string;
  hazardousWeatherExpires: string;
}

const HAZARDOUS_WEATHER_EXPORT_LABELS: Record<HazardousWeatherEvaluation['status'], string> = {
  current: 'Current',
  due_soon: 'Due Soon',
  expired: 'Expired',
  missing: 'Missing',
  not_applicable: 'N/A',
};

const LEADER_EXPORT_COLUMNS: Column<LeaderExportRow>[] = [
  { label: 'Name', get: (r) => r.name },
  { label: 'Email', get: (r) => r.email },
  { label: 'Phone', get: (r) => r.phone },
  { label: 'District(s)', get: (r) => r.districts },
  { label: 'Scope', get: (r) => r.scope },
  { label: 'Where', get: (r) => r.where },
  { label: 'Positions', get: (r) => r.positions },
  { label: 'Untrained positions', get: (r) => r.untrainedPositions },
  { label: 'Courses still owed', get: (r) => r.incompleteCourses },
  { label: 'Any untrained', get: (r) => r.anyUntrained },
  { label: 'SYT current', get: (r) => r.sytCurrent },
  { label: 'SYT expires', get: (r) => r.sytExpires },
  { label: 'Registration expires', get: (r) => r.registrationExpires },
  { label: 'Hazardous Weather status', get: (r) => r.hazardousWeatherStatus },
  { label: 'Hazardous Weather completed', get: (r) => r.hazardousWeatherCompleted },
  { label: 'Hazardous Weather expires', get: (r) => r.hazardousWeatherExpires },
];

// ---- page ----------------------------------------------------------

type SortKey = 'name' | 'district' | 'youth' | 'recharter' | 'advancement';

/** Navigation state the dashboard drill-downs hand over. */
interface UhNavState {
  uhFilter?: UnitFilter | LeaderFilter;
  uhView?: 'units' | 'leaders';
}

// ---- pagination (display-only — never limits search/filter/export) -----

const PAGE_SIZE = 50;

/** Display-only Previous/Next pager. Never changes what's searched, filtered
 *  or exported — only which slice of an already-computed row set is drawn. */
function PaginationBar({
  page,
  pageCount,
  totalCount,
  onPage,
}: {
  page: number;
  pageCount: number;
  totalCount: number;
  onPage: (p: number) => void;
}) {
  if (totalCount === 0) return null;
  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, totalCount);
  return (
    <div className="uh-pagination">
      <span className="uh-pagination-range">
        {start}–{end} of {totalCount}
      </span>
      <div className="uh-pagination-controls">
        <button
          type="button"
          className="btn"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Previous
        </button>
        <span className="uh-pagination-pagelabel">
          Page {page} of {pageCount}
        </span>
        <button
          type="button"
          className="btn primary"
          disabled={page >= pageCount}
          onClick={() => onPage(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

// ---- leader export rows, computed on demand (not eagerly on every render) ----
// Export-specific transforms only need to run when an export is actually
// requested, or while the Leaders/Attention tab is on screen — never just
// because Unit Health is mounted on Units/Charters. See UnitHealth()'s
// `ensureSytLoaded`/`buildLeaderExportRowsForExport` for how these are used.

function computeFallbackLeaderRows(
  allLeaders: UnitLeader[],
  districts: NamedRecord[],
  districtFilter: string,
  leaderFilter: LeaderFilter,
  sytByMember: Map<string, UnitSyt>,
): MergedLeader[] {
  const merged = mergeLeaders(allLeaders, districts);
  return merged
    .filter((m) => districtFilter === 'All' || m.districtIds.includes(districtFilter))
    .filter((m) => leaderMatchesFilter(m, leaderFilter, sytByMember))
    .sort(
      (a, b) =>
        SCOPE_RANK[a.scopes[0]] - SCOPE_RANK[b.scopes[0]] || a.lastName.localeCompare(b.lastName),
    );
}

function computeLeaderExportRows(
  leaderRows: MergedLeader[],
  sytByMember: Map<string, UnitSyt>,
  districts: NamedRecord[],
): LeaderExportRow[] {
  return leaderRows.map((m) => {
    const s = sytByMember.get(m.memberId);
    const untrained = m.roles.filter((r) => !r.trained).map((r) => `${r.position} (${r.where})`);
    const courses = [...new Set(m.roles.flatMap((r) => r.incompleteCourses))];
    const hw = evaluateHazardousWeather(m.roles);
    return {
      name: m.fullName,
      email: m.email,
      phone: s?.phone ?? '',
      districts: m.districtIds.map((id) => recName(districts, id)).join('; '),
      scope: m.scopes.join('; '),
      where: m.places.join('; '),
      positions: m.roles
        .map((r) => `${r.position} (${r.where})${r.trained ? '' : ' — untrained'}`)
        .join('; '),
      untrainedPositions: untrained.join('; '),
      incompleteCourses: courses.join('; '),
      anyUntrained: yn(m.anyUntrained),
      sytCurrent: s ? yn(s.isCurrent) : 'no record',
      sytExpires: s?.expiresOn ?? '',
      registrationExpires: m.registrationExpires ?? '',
      hazardousWeatherStatus: HAZARDOUS_WEATHER_EXPORT_LABELS[hw.status],
      hazardousWeatherCompleted: hw.completedDate ?? '',
      hazardousWeatherExpires: hw.expirationDate ?? '',
    };
  });
}

export default function UnitHealth() {
  const { profile, settings, districtFilter } = useAppContext();
  const admin = profile.role === 'admin';
  const location = useLocation();
  const navigate = useNavigate();
  const nav = (location.state as UhNavState | null) ?? null;
  const unitsQ = useVisibleUnits(profile);
  const importsQ = useUnitImports();
  const leadersQ = useVisibleUnitLeaders(profile);
  const actionsQ = useVisibleActions(profile);
  // Who commissions which unit (read-only references; names are looked up only for the unit drawer that is open).
  const commissionersQ = useVisibleUnitCommissioners(profile);
  const commissionersByUnit = useMemo(() => linksByUnit(commissionersQ.data), [commissionersQ.data]);
  // A unit counts as covered when it has any commissioner relationship, or an operational commissioner designated (the manual
  // assignment), each unit once. If the relationships cannot be read at all (no access, or a failed read), coverage is "Unavailable",
  // never a false 0%.
  const coverageUnits = useMemo(() => coverageByUnit(commissionersQ.data, unitsQ.data), [commissionersQ.data, unitsQ.data]);
  const unitsWithCommissioner = useMemo(() => new Set(coverageUnits.keys()), [coverageUnits]);
  const commissionerDataAvailable = !commissionersQ.loading && !commissionersQ.error;

  // SYT is a large, council-wide collection that the Units and Charters
  // tabs never actually read — it's only needed by the Leaders tab, the
  // Attention tab (leadership-sustainability readiness/SYT signals), or an
  // open unit drawer (its Leadership Sustainability block + Export Unit).
  // `sytNeeded` starts true only if navigation landed straight on Leaders,
  // and otherwise flips on the first time one of those is actually used —
  // and then stays on for the rest of the session (no reason to tear the
  // listener back down once paid for).
  const [sytNeeded, setSytNeeded] = useState(nav?.uhView === 'leaders');
  const sytQ = useVisibleSyt(profile, sytNeeded);
  const sytLoading = sytNeeded && sytQ.loading;

  // Open (not completed/cancelled) system Advancement Stalled Actions,
  // keyed by unit — Unit Health stays the diagnostic view, but a Stalled
  // unit with real accountable follow-up already in progress gets a
  // subtle "Action open" link into the actual work queue (Actions).
  const openAdvancementActionByUnit = useMemo(() => {
    const m = new Map<string, Action>();
    for (const a of actionsQ.data) {
      if (a.source !== 'system' || a.sourceType !== 'advancementStalled' || !a.relatedUnitId) continue;
      if (a.status === 'completed' || a.status === 'cancelled') continue;
      m.set(a.relatedUnitId, a);
    }
    return m;
  }, [actionsQ.data]);

  // Same pattern for Unit Renewal (Phase 4, sourceType 'recharter') — a
  // unit that's overdue on recharter with real follow-up already in
  // progress gets the same subtle "Action open" link.
  const openRechartActionByUnit = useMemo(() => {
    const m = new Map<string, Action>();
    for (const a of actionsQ.data) {
      if (a.source !== 'system' || a.sourceType !== 'recharter' || !a.relatedUnitId) continue;
      if (a.status === 'completed' || a.status === 'cancelled') continue;
      m.set(a.relatedUnitId, a);
    }
    return m;
  }, [actionsQ.data]);

  // Leadership Sustainability's Leadership Depth / Program Leadership /
  // Role Concentration sections must read "Insufficient Data" (never a
  // false zero) when Compass has never received a Trained Leaders report
  // for this tenant at all — see computeLeadershipDepth() in
  // lib/leadershipSustainability.ts.
  const trainedLeadersImportExists = useMemo(
    () => importsQ.data.some((i) => i.reportType === 'trainedLeaders'),
    [importsQ.data],
  );

  const [exporting, setExporting] = useState(false);
  // What the Leaders tab is currently showing (null until that tab mounts).
  const [leaderRowsFromView, setLeaderRowsFromView] = useState<MergedLeader[] | null>(null);
  const [openUnit, setOpenUnit] = useState<Unit | null>(null);
  const [view, setView] = useState<'units' | 'leaders' | 'charters' | 'attention'>(
    nav?.uhView === 'leaders' ? 'leaders' : 'units',
  );
  const [filter, setFilter] = useState<UnitFilter>(
    nav?.uhView !== 'leaders' && nav?.uhFilter ? (nav.uhFilter as UnitFilter) : 'all',
  );
  const [leaderFilter, setLeaderFilter] = useState<LeaderFilter>(
    nav?.uhView === 'leaders' && nav?.uhFilter ? (nav.uhFilter as LeaderFilter) : 'all',
  );
  const [unitQuery, setUnitQuery] = useState('');
  const [unitTypes, setUnitTypes] = useState<Set<UnitType>>(() => new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'recharter', dir: 1 });
  const toggleType = (t: UnitType) =>
    setUnitTypes((cur) => {
      const next = new Set(cur);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });

  // Off the Leaders tab, fall back to the no-search leader set for exports.
  useEffect(() => {
    if (view !== 'leaders') setLeaderRowsFromView(null);
  }, [view]);

  // Re-apply when the dashboard links here again while already mounted.
  useEffect(() => {
    if (!nav?.uhFilter && !nav?.uhView) return;
    if (nav.uhView === 'leaders') {
      setView('leaders');
      setLeaderFilter((nav.uhFilter as LeaderFilter) ?? 'all');
    } else {
      setView('units');
      setFilter((nav.uhFilter as UnitFilter) ?? 'all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Turn on the SYT fetch the first time it's genuinely needed (see the
  // `sytNeeded` comment above), and leave it on once triggered.
  useEffect(() => {
    if (view === 'leaders' || view === 'attention' || openUnit) setSytNeeded(true);
  }, [view, openUnit]);

  // Refs so the async export handlers below always read post-await data,
  // not whatever was in scope (or in a since-recreated callback's closure)
  // at the moment the export button was clicked.
  const sytByMemberRef = useRef<Map<string, UnitSyt>>(new Map());
  const leadersDataRef = useRef<UnitLeader[]>(leadersQ.data);
  const leaderRowsFromViewRef = useRef<MergedLeader[] | null>(leaderRowsFromView);
  useEffect(() => {
    leadersDataRef.current = leadersQ.data;
  }, [leadersQ.data]);
  useEffect(() => {
    leaderRowsFromViewRef.current = leaderRowsFromView;
  }, [leaderRowsFromView]);

  // Lets an export (which always needs a complete Leaders sheet, regardless
  // of which tab is active) or a just-opened unit drawer wait for a SYT
  // fetch it triggers itself — resolves with the fresh data (read from
  // sytByMemberRef at resolve time) rather than a value closed over at call
  // time, which would go stale if SYT was still loading at the moment of
  // the call.
  //
  // `prevSytNeededRef` guards against a one-render race: the render where
  // `sytNeeded` first flips true is the SAME render on which useCollection's
  // `loading` flag hasn't been flipped to true yet either (that update only
  // lands via useCollection's own effect, one tick later) — so on that one
  // render `sytQ.loading` is still stale `false`. Without this guard, a
  // waiter queued on that exact render would resolve immediately with
  // pre-fetch (often empty) data instead of actually waiting for the fetch.
  const sytWaitersRef = useRef<Array<() => void>>([]);
  const prevSytNeededRef = useRef(sytNeeded);
  useEffect(() => {
    const justBecameNeeded = sytNeeded && !prevSytNeededRef.current;
    prevSytNeededRef.current = sytNeeded;
    if (sytNeeded && !sytQ.loading && !justBecameNeeded && sytWaitersRef.current.length > 0) {
      const waiters = sytWaitersRef.current;
      sytWaitersRef.current = [];
      for (const w of waiters) w();
    }
  }, [sytNeeded, sytQ.loading]);
  const ensureSytLoaded = useCallback((): Promise<Map<string, UnitSyt>> => {
    // Safe to trust `!sytQ.loading` here without the same-render guard the
    // effect above needs: this only ever runs from a click handler, which
    // can't fire until after the browser has painted a committed render —
    // by then any pending sytNeeded transition has already been through a
    // full render+effect cycle, so sytQ.loading is never the stale value.
    if (sytNeeded && !sytQ.loading) return Promise.resolve(sytByMemberRef.current);
    setSytNeeded(true);
    return new Promise<Map<string, UnitSyt>>((resolve) => {
      sytWaitersRef.current.push(() => resolve(sytByMemberRef.current));
    });
  }, [sytNeeded, sytQ.loading]);

  const [unitsPage, setUnitsPage] = useState(1);
  useEffect(() => {
    setUnitsPage(1);
  }, [filter, unitQuery, unitTypes, districtFilter]);

  const leadersByUnit = useMemo(() => {
    const map = new Map<string, UnitLeader[]>();
    for (const l of leadersQ.data) {
      if (!l.unitId) continue;
      const arr = map.get(l.unitId) ?? [];
      arr.push(l);
      map.set(l.unitId, arr);
    }
    return map;
  }, [leadersQ.data]);
  const sytByMember = useMemo(() => {
    const map = new Map<string, UnitSyt>();
    for (const s of sytQ.data) map.set(s.memberId, s);
    return map;
  }, [sytQ.data]);
  useEffect(() => {
    sytByMemberRef.current = sytByMember;
  }, [sytByMember]);

  const trainedRatio = (unitId: string) => {
    const list = leadersByUnit.get(unitId) ?? [];
    if (list.length === 0) return null;
    return { trained: list.filter((l) => !l.anyUntrained).length, total: list.length };
  };

  // Latest advancement is denormalized on the unit doc (see src/lib/units.ts) —
  // `advForUnit` / `advForUnitProgram` read it straight off `unit`.

  // District-scoped + sorted, before the exception-filter chips. The summary
  // scorecard counts this list; the table shows `units` (below).
  const districtUnits = useMemo(() => {
    let list = [...unitsQ.data];
    if (districtFilter !== 'All') list = list.filter((u) => u.districtId === districtFilter);
    const youthOf = (u: Unit) => advForUnit(u)?.youth ?? u.recharter?.paidYouth ?? 0;
    list.sort((a, b) => {
      const m = sort.dir;
      switch (sort.key) {
        case 'name':
          return a.name.localeCompare(b.name) * m;
        case 'district':
          return (
            recName(settings.districts, a.districtId).localeCompare(
              recName(settings.districts, b.districtId),
            ) * m || a.name.localeCompare(b.name)
          );
        case 'youth':
          return (youthOf(a) - youthOf(b)) * m;
        case 'recharter': {
          const av = a.recharter ? daysUntil(a.recharter.charterExpires) : 99999;
          const bv = b.recharter ? daysUntil(b.recharter.charterExpires) : 99999;
          return (av - bv) * m;
        }
        case 'advancement': {
          const rank = { red: 0, amber: 1, ok: 2 };
          const av = advancementState(a, advForUnit(a));
          const bv = advancementState(b, advForUnit(b));
          return ((av ? rank[av.tone] : 3) - (bv ? rank[bv.tone] : 3)) * m;
        }
      }
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitsQ.data, districtFilter, sort, settings.districts]);

  const presentTypes = useMemo(
    () => UNIT_TYPE_ORDER.filter((t) => districtUnits.some((u) => u.type === t)),
    [districtUnits],
  );

  const units = useMemo(() => {
    const needle = unitQuery.trim().toLowerCase();
    let list =
      filter === 'all'
        ? districtUnits
        : districtUnits.filter((u) => unitMatchesFilter(u, filter, advForUnit(u), unitsWithCommissioner));
    if (unitTypes.size) list = list.filter((u) => unitTypes.has(u.type));
    if (needle) {
      list = list.filter(
        (u) =>
          u.name.toLowerCase().includes(needle) ||
          u.type.toLowerCase().includes(needle) ||
          (u.charterOrg ?? '').toLowerCase().includes(needle),
      );
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtUnits, filter, unitQuery, unitTypes, unitsWithCommissioner]);

  // Display-only 50/page slice of `units` — search/filter/sort above still
  // run against the full authorized set; only the table's rendered rows are
  // paginated. Clamped so a filter change that shrinks the result set can't
  // strand the page past the end.
  const unitsPageCount = Math.max(1, Math.ceil(units.length / PAGE_SIZE));
  const unitsPageClamped = Math.min(unitsPage, unitsPageCount);
  const pagedUnits = useMemo(
    () => units.slice((unitsPageClamped - 1) * PAGE_SIZE, unitsPageClamped * PAGE_SIZE),
    [units, unitsPageClamped],
  );

  const summary = useMemo(() => {
    let youth = 0;
    let eagles = 0;
    let rechartOverdue = 0;
    let rechartDue60 = 0;
    let yptFlag = 0;
    let leaderGap = 0;
    let stalled = 0;
    for (const u of districtUnits) {
      const adv = advForUnit(u);
      youth += adv?.youth ?? u.recharter?.paidYouth ?? 0;
      const bsa = advForUnitProgram(u, 'scoutsBSA');
      eagles += bsa?.eaglesYtd ?? 0;
      const rc = rechartCountdown(u);
      if (rc && rc.days < 0) rechartOverdue++;
      else if (rc && rc.days <= 60) rechartDue60++;
      if (u.key3 && (u.key3.worstYpt === 'expired' || u.key3.worstYpt === 'expiring')) yptFlag++;
      const lg = leadershipTone(u);
      if (lg && lg.missing.length > 0) leaderGap++;
      const as = advancementState(u, adv);
      if (as?.label === 'Stalled') stalled++;
    }
    return {
      count: districtUnits.length,
      youth,
      eagles,
      rechartOverdue,
      rechartDue60,
      yptFlag,
      leaderGap,
      stalled,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtUnits]);

  const coverage = useMemo(() => commissionerCoverage(districtUnits, coverageUnits), [districtUnits, coverageUnits]);

  // ---- export ----------------------------------------------------
  const unitExportRows = useMemo<UnitExportRow[]>(
    () =>
      units.map((u) => {
        const adv = advForUnit(u);
        const bsa = advForUnitProgram(u, 'scoutsBSA');
        const cub = advForUnitProgram(u, 'cub');
        const rc = rechartCountdown(u);
        const lt = leadershipTone(u);
        const as = advancementState(u, adv);
        const tr = trainedRatio(u.id);
        return {
          district: recName(settings.districts, u.districtId),
          unit: u.name,
          type: u.type,
          youth: adv?.youth ?? u.recharter?.paidYouth ?? 0,
          charterExpires: u.recharter?.charterExpires ?? '',
          daysToRecharter: rc ? rc.days : '',
          recharterStatus: !u.recharter
            ? 'no report'
            : rc && rc.days < 0
              ? 'overdue'
              : rc && rc.days <= 60
                ? 'due ≤ 60 days'
                : 'chartered',
          committeeChair: u.key3 ? yn(u.key3.hasCommitteeChair) : '',
          unitLeader: u.key3 ? yn(u.key3.hasUnitLeader) : '',
          charterOrgRep: u.key3 ? yn(u.key3.hasCharterOrgRep) : '',
          leadershipGap: lt ? lt.missing.join('; ') : '',
          key3Syt: u.key3 ? u.key3.worstYpt : '',
          trained: tr ? `${tr.trained}/${tr.total}` : '',
          advancement: as ? as.label : '',
          scoutsBsaRanksYtd: bsa ? bsa.ranksYtd : '',
          cubRanksYtd: cub ? cub.ranksYtd : '',
          eaglesYtd: bsa ? bsa.eaglesYtd : '',
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [units, settings.districts],
  );

  const districtTag =
    districtFilter === 'All'
      ? 'all-districts'
      : recName(settings.districts, districtFilter).toLowerCase().replace(/\s+/g, '-');

  // Computed only when an export actually runs — not on every render, and
  // not just because Unit Health happens to be mounted. Waits for SYT if it
  // hasn't loaded yet (e.g. exporting straight from Units without ever
  // opening Leaders/Attention), reading post-wait data via refs so this
  // never ships an export built from stale, pre-fetch state. Mirrors the
  // Leaders tab's own filtered rows when that tab has been opened this
  // session (`leaderRowsFromView`); otherwise falls back to a district +
  // quick-filter view (no free-text search) so the workbook's Leaders sheet
  // is never empty just because Leaders was never opened.
  const buildLeaderExportRowsForExport = useCallback(async (): Promise<LeaderExportRow[]> => {
    const freshSytByMember = await ensureSytLoaded();
    const sourceRows =
      leaderRowsFromViewRef.current ??
      computeFallbackLeaderRows(
        leadersDataRef.current,
        settings.districts,
        districtFilter,
        leaderFilter,
        freshSytByMember,
      );
    return computeLeaderExportRows(sourceRows, freshSytByMember, settings.districts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ensureSytLoaded, settings.districts, districtFilter, leaderFilter]);

  const [csvExporting, setCsvExporting] = useState(false);

  const exportCsv = async () => {
    if (view === 'charters') return;
    if (view === 'units') {
      downloadCsv(
        unitExportRows,
        UNIT_EXPORT_COLUMNS,
        `unit-health-units-${districtTag}-${stamp()}.csv`,
      );
      return;
    }
    setCsvExporting(true);
    try {
      const rows = await buildLeaderExportRowsForExport();
      downloadCsv(rows, LEADER_EXPORT_COLUMNS, `unit-health-leaders-${districtTag}-${stamp()}.csv`);
    } finally {
      setCsvExporting(false);
    }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const leaderRows = await buildLeaderExportRowsForExport();
      await downloadXlsx(
        [
          { name: 'Units', rows: unitExportRows, columns: UNIT_EXPORT_COLUMNS },
          { name: 'Leaders', rows: leaderRows, columns: LEADER_EXPORT_COLUMNS },
        ],
        `unit-health-${districtTag}-${stamp()}.xlsx`,
      );
    } finally {
      setExporting(false);
    }
  };

  const lastImport = (t: UnitReportType) => importsQ.data.find((i) => i.reportType === t) ?? null;

  const sortBtn = (key: SortKey, label: string) => (
    <button
      className="uh-sortbtn"
      onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : 1 }))}
    >
      {label}
      {sort.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
    </button>
  );

  const showDistrict = districtFilter === 'All';

  return (
    <>
      <section className="iv-intro">
        <div>
          <h2>Unit Health</h2>
          <p>
            Recharter, Safeguarding Youth Training, leadership, and advancement — from your imported
            reports.
          </p>
        </div>
        <div className="uh-actions">
          <button
            className="btn"
            onClick={exportCsv}
            disabled={view === 'charters' || csvExporting}
            title={
              view === 'charters'
                ? 'Charters export isn\'t available yet — switch to Units or Leaders to export.'
                : view === 'units'
                  ? `Downloads the ${unitExportRows.length} unit row(s) matching your current district, search and filters`
                  : `Downloads authorized leader rows matching your current district and filters${leaderRowsFromView ? ` (${leaderRowsFromView.length})` : ''} — not just the rows on this page`
            }
          >
            {csvExporting ? <Spinner size={14} /> : 'Export CSV'}
          </button>
          <button
            className="btn"
            onClick={exportXlsx}
            disabled={exporting}
            title="Downloads an Excel workbook with a Units sheet and a Leaders sheet — every authorized row matching your current district and filters, not just the rows on the current page"
          >
            {exporting ? <Spinner size={14} /> : 'Export Excel'}
          </button>
          {admin && (
            <button
              className="btn primary"
              onClick={() => navigate('/settings', { state: { openTab: 'dataImports' } })}
              title="Importing CSV reports now lives in Settings → Data Imports"
            >
              Manage Data Imports
            </button>
          )}
        </div>
      </section>

      <div className="card uh-lastimport">
        <div className="uh-datastatus-head">
          <b>Data Status</b>
          <span className="subtitle">
            When each underlying report was last successfully imported — if Compass isn't
            showing a change you expected from my.Scouting, check here first.
            {admin && ' Admins: manage imports in Settings → Data Imports.'}
          </span>
        </div>
        {(Object.keys(UNIT_REPORT_LABELS) as UnitReportType[]).map((t) => {
          const li = lastImport(t);
          return (
            <span key={t}>
              <b>{UNIT_REPORT_LABELS[t]}:</b>{' '}
              {li ? li.reportDate : <span className="subtitle">not imported</span>}
            </span>
          );
        })}
      </div>

      <div className="uh-viewtabs">
        <button className={view === 'units' ? 'active' : ''} onClick={() => setView('units')}>
          Units
        </button>
        <button className={view === 'leaders' ? 'active' : ''} onClick={() => setView('leaders')}>
          Leaders
        </button>
        <button className={view === 'charters' ? 'active' : ''} onClick={() => setView('charters')}>
          Charters
        </button>
        <button className={view === 'attention' ? 'active' : ''} onClick={() => setView('attention')}>
          Attention
        </button>
        <span className="uh-exporthint">
          Export downloads every row matching your district, search and filters — not just the
          rows on the current page.
        </span>
      </div>

      {view === 'attention' && (
        <AttentionView
          profile={profile}
          units={districtUnits}
          leadersByUnit={leadersByUnit}
          sytByMember={sytByMember}
          sytLoading={sytLoading}
          trainedLeadersImportExists={trainedLeadersImportExists}
          districtFilter={districtFilter}
          settings={settings}
          onOpenUnit={setOpenUnit}
        />
      )}

      {view === 'leaders' && (
        <LeadersView
          leaders={leadersQ.data}
          sytByMember={sytByMember}
          sytLoading={sytLoading}
          districtFilter={districtFilter}
          settings={settings}
          filter={leaderFilter}
          onFilter={setLeaderFilter}
          onRows={setLeaderRowsFromView}
        />
      )}

      {view === 'charters' && (
        <CharterOrgsView
          units={unitsQ.data}
          districtFilter={districtFilter}
          settings={settings}
          onOpenUnit={setOpenUnit}
        />
      )}

      {view === 'units' && unitsQ.loading && <div className="empty"><Spinner label="Loading…" /></div>}
      {view === 'units' && !unitsQ.loading && districtUnits.length === 0 && (
        <div className="card empty">
          No units yet.{admin ? ' Use "Import data" to load your reports.' : ''}
        </div>
      )}

      {view === 'units' && districtUnits.length > 0 && (
        <>
          <div className="card uh-summary">
            <div>
              <div className="subtitle">Units</div>
              <b>{summary.count}</b>
            </div>
            <div>
              <div className="subtitle">Youth</div>
              <b>{summary.youth}</b>
            </div>
            <div>
              <div className="subtitle">Eagles YTD</div>
              <b>{summary.eagles}</b>
            </div>
            <SumFilterTile
              label="Recharter overdue"
              n={summary.rechartOverdue}
              f="recharterOverdue"
              active={filter}
              onToggle={setFilter}
            />
            <SumFilterTile
              label="Recharter ≤ 60d"
              n={summary.rechartDue60}
              f="recharterDue60"
              active={filter}
              onToggle={setFilter}
            />
            <SumFilterTile
              label="SYT flags"
              n={summary.yptFlag}
              f="sytFlag"
              active={filter}
              onToggle={setFilter}
            />
            <SumFilterTile
              label="Leadership gaps"
              n={summary.leaderGap}
              f="leadershipGap"
              active={filter}
              onToggle={setFilter}
            />
            <SumFilterTile
              label="Stalled"
              n={summary.stalled}
              f="stalled"
              active={filter}
              onToggle={setFilter}
            />
            <div>
              <div className="subtitle">Commissioner coverage</div>
              {commissionerDataAvailable && coverage.percent !== null ? (
                <>
                  <b>{coverage.percent}%</b>
                  <div className="subtitle">
                    {coverage.withCommissioner} of {coverage.total} units
                  </div>
                </>
              ) : (
                <b title={commissionersQ.loading ? 'Loading commissioner assignments…' : 'Commissioner assignments are not available to you.'}>
                  {commissionersQ.loading ? '…' : 'Unavailable'}
                </b>
              )}
            </div>
            {commissionerDataAvailable && (
              <SumFilterTile
                label="No commissioner"
                n={coverage.without}
                f="noCommissioner"
                active={filter}
                onToggle={setFilter}
              />
            )}
          </div>

          <div className="uh-leadertools">
            <input
              placeholder="Search unit name or number…"
              value={unitQuery}
              onChange={(e) => setUnitQuery(e.target.value)}
            />
            {presentTypes.length > 1 && (
              <div className="uh-leaderchips">
                {presentTypes.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`uh-chip${unitTypes.has(t) ? ' is-on' : ''}`}
                    aria-pressed={unitTypes.has(t)}
                    onClick={() => toggleType(t)}
                  >
                    {UNIT_TYPE_PLURAL[t]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {(filter !== 'all' || unitQuery.trim() || unitTypes.size > 0) && (
            <div className="uh-filterbar">
              Showing <b>{units.length}</b> of {districtUnits.length}
              {filter !== 'all' && (
                <>
                  {' — '}
                  <span className="uh-filterchip">{UNIT_FILTER_LABELS[filter]}</span>
                </>
              )}
              {unitTypes.size > 0 && (
                <>
                  {' — '}
                  <span className="uh-filterchip">
                    {UNIT_TYPE_ORDER.filter((t) => unitTypes.has(t))
                      .map((t) => UNIT_TYPE_PLURAL[t])
                      .join(', ')}
                  </span>
                </>
              )}
              {unitQuery.trim() && (
                <>
                  {' — '}
                  <span className="uh-filterchip">“{unitQuery.trim()}”</span>
                </>
              )}
              <button
                className="uh-filterclear"
                onClick={() => {
                  setFilter('all');
                  setUnitQuery('');
                  setUnitTypes(new Set());
                }}
              >
                Clear ✕
              </button>
            </div>
          )}

          {units.length === 0 ? (
            <div className="card empty">No units match in the current district.</div>
          ) : (
          <div className="card" style={{ overflowX: 'auto' }}>
            <table className="uh-table">
              <thead>
                <tr>
                  <th>{sortBtn('name', 'Unit')}</th>
                  {showDistrict && <th>{sortBtn('district', 'District')}</th>}
                  <th>{sortBtn('youth', 'Youth')}</th>
                  <th>{sortBtn('recharter', 'Recharter')}</th>
                  <th>SYT</th>
                  <th>Leaders</th>
                  <th>Trained</th>
                  <th>
                    {sortBtn('advancement', 'Advancement (YTD)')}
                    <InfoTip label="What Advancement pace and “Stalled” mean">
                      Rates a Pack or Troop against its latest advancement report.
                      <br />
                      <br />
                      <b>Stalled</b> — registered youth, but <b>zero rank advancements
                      recorded this program year</b>.
                      <br />
                      <b>Behind</b> — a Pack that has earned ranks for fewer than half its
                      youth.
                      <br />
                      <b>On track</b> — anything else with a report.
                      <br />
                      <br />
                      Crews, Ships, Posts and Clubs have no rank ladder, so they show
                      “—”. A unit with no report imported also shows “—”, not Stalled.
                    </InfoTip>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pagedUnits.map((u) => {
                  const rc = rechartCountdown(u);
                  const lt = leadershipTone(u);
                  const adv = advForUnit(u);
                  const as = advancementState(u, adv);
                  const yptTone = u.key3 ? YPT_TONE[u.key3.worstYpt] : null;
                  const tr = trainedRatio(u.id);
                  return (
                    <tr key={u.id} className="uh-row" onClick={() => setOpenUnit(u)}>
                      <td>
                        <b>{u.name}</b>
                        <div className="subtitle" style={{ textTransform: 'capitalize' }}>
                          {u.type}
                        </div>
                      </td>
                      {showDistrict && <td>{recName(settings.districts, u.districtId)}</td>}
                      <td>{adv?.youth ?? u.recharter?.paidYouth ?? '—'}</td>
                      <td>
                        {rc ? (
                          <span className="uh-cell">
                            <span className={`uh-pill uh-${rc.tone}`}>
                              {rc.days < 0 ? 'overdue' : `${rc.days}d`}
                            </span>
                            {openRechartActionByUnit.has(u.id) && (
                              <button
                                type="button"
                                className="uh-action-open-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate('/actions', { state: { openActionId: openRechartActionByUnit.get(u.id)!.id } });
                                }}
                              >
                                Action open
                              </button>
                            )}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {yptTone ? (
                          <span className="uh-cell uh-help" title={sytTooltip(u)}>
                            <ToneDot tone={yptTone} />
                            {u.key3!.worstYpt}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {lt ? (
                          <span className="uh-cell uh-help" title={leadershipTooltip(lt)}>
                            <ToneDot tone={lt.tone} />
                            {lt.missing.length ? `missing ${lt.missing.length}` : 'complete'}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {tr ? (
                          <span
                            className="uh-cell uh-help"
                            title={`${tr.trained} of ${tr.total} registered leaders are fully trained for every position they hold (from the Trained Leaders report).`}
                          >
                            <ToneDot
                              tone={
                                tr.trained === tr.total
                                  ? 'ok'
                                  : tr.trained >= tr.total * 0.7
                                    ? 'amber'
                                    : 'red'
                              }
                            />
                            {tr.trained}/{tr.total}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {as ? (
                          <span className="uh-cell uh-help" title={advancementTooltip(u, as)}>
                            <ToneDot tone={as.tone} />
                            {as.label} · {adv!.ranksYtd}
                            {as.label === 'Stalled' && openAdvancementActionByUnit.has(u.id) && (
                              <button
                                type="button"
                                className="uh-action-open-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate('/actions', { state: { openActionId: openAdvancementActionByUnit.get(u.id)!.id } });
                                }}
                              >
                                Action open
                              </button>
                            )}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <PaginationBar
              page={unitsPageClamped}
              pageCount={unitsPageCount}
              totalCount={units.length}
              onPage={setUnitsPage}
            />
          </div>
          )}
        </>
      )}

      {openUnit && (
        <UnitDrawer
          unit={openUnit}
          districtName={recName(settings.districts, openUnit.districtId)}
          leaders={leadersByUnit.get(openUnit.id) ?? []}
          commissionerLinks={commissionersByUnit.get(openUnit.id) ?? []}
          commissionerDataAvailable={commissionerDataAvailable}
          sytByMember={sytByMember}
          sytLoading={sytLoading}
          ensureSytLoaded={ensureSytLoaded}
          trainedLeadersImportExists={trainedLeadersImportExists}
          onClose={() => setOpenUnit(null)}
          openRechartAction={openRechartActionByUnit.get(openUnit.id) ?? null}
          onOpenAction={(actionId) => navigate('/actions', { state: { openActionId: actionId } })}
        />
      )}
    </>
  );
}

// ---- leaders view -------------------------------------------------

const SCOPE_RANK: Record<LeaderScope, number> = { council: 0, district: 1, unit: 2 };

interface LeaderRole {
  position: string;
  trained: boolean;
  directContact: boolean;
  incompleteCourses: string[];
  scope: LeaderScope;
  where: string;
}
interface MergedLeader {
  memberId: string;
  fullName: string;
  lastName: string;
  email: string;
  scopes: LeaderScope[]; // distinct, senior-first
  places: string[]; // distinct where-labels, senior-first
  roles: LeaderRole[];
  anyUntrained: boolean;
  registrationExpires: string | null;
  reportDate: string;
  districtIds: string[];
}

/** One row per person — the Trained Leaders report has a row per registered
 *  position, so a person shows up several times (Scoutmaster of a troop AND
 *  a district commissioner AND on the council board). Fold those into a
 *  single directory entry; the drawer breaks the roles back out by where. */
function mergeLeaders(
  leaders: UnitLeader[],
  districts: { id: string; name: string }[],
): MergedLeader[] {
  const whereOf = (l: UnitLeader) =>
    l.scope === 'council'
      ? 'Council'
      : l.scope === 'district'
        ? recName(districts, l.districtId ?? '')
        : l.unitName ?? 'a unit';

  const byMember = new Map<string, MergedLeader>();
  for (const l of leaders) {
    const where = whereOf(l);
    const roles: LeaderRole[] = l.positions.map((p) => ({
      position: p.position,
      trained: p.trained,
      directContact: p.directContact,
      incompleteCourses: p.incompleteCourses,
      scope: l.scope,
      where,
    }));
    const cur = byMember.get(l.memberId);
    if (!cur) {
      byMember.set(l.memberId, {
        memberId: l.memberId,
        fullName: l.fullName,
        lastName: l.lastName,
        email: l.email,
        scopes: [l.scope],
        places: [where],
        roles,
        anyUntrained: l.anyUntrained,
        registrationExpires: l.registrationExpires,
        reportDate: l.reportDate,
        districtIds: l.districtId ? [l.districtId] : [],
      });
      continue;
    }
    if (!cur.scopes.includes(l.scope)) cur.scopes.push(l.scope);
    if (!cur.places.includes(where)) cur.places.push(where);
    cur.roles.push(...roles);
    cur.anyUntrained = cur.anyUntrained || l.anyUntrained;
    if (
      l.registrationExpires &&
      (!cur.registrationExpires || l.registrationExpires < cur.registrationExpires)
    ) {
      cur.registrationExpires = l.registrationExpires;
    }
    if (l.districtId && !cur.districtIds.includes(l.districtId)) cur.districtIds.push(l.districtId);
    if (!cur.email && l.email) cur.email = l.email;
  }

  for (const m of byMember.values()) {
    m.scopes.sort((a, b) => SCOPE_RANK[a] - SCOPE_RANK[b]);
    const placeRank = (w: string) =>
      Math.min(...m.roles.filter((r) => r.where === w).map((r) => SCOPE_RANK[r.scope]));
    m.places.sort((a, b) => placeRank(a) - placeRank(b));
    m.roles.sort(
      (a, b) => SCOPE_RANK[a.scope] - SCOPE_RANK[b.scope] || a.where.localeCompare(b.where),
    );
  }
  return [...byMember.values()];
}

/** A summary scorecard tile that doubles as a filter toggle. */
function SumFilterTile({
  label,
  n,
  f,
  active,
  onToggle,
}: {
  label: string;
  n: number;
  f: UnitFilter;
  active: UnitFilter;
  onToggle: (updater: (cur: UnitFilter) => UnitFilter) => void;
}) {
  const on = active === f;
  return (
    <button
      type="button"
      className={`uh-sumtile${on ? ' is-on' : ''}`}
      aria-pressed={on}
      onClick={() => onToggle((cur) => (cur === f ? 'all' : f))}
    >
      <div className="subtitle">{label}</div>
      <b className={n ? 'uh-num-red' : ''}>{n}</b>
    </button>
  );
}

interface CharterOrgGroup {
  name: string;
  orgType: string | null;
  address: string | null;
  cityState: string | null;
  zip: string | null;
  phone: string | null;
  districtIds: string[];
  units: Unit[];
}

function groupByCharterOrg(units: Unit[], districtFilter: string): CharterOrgGroup[] {
  const inDistrict = units.filter((u) => districtFilter === 'All' || u.districtId === districtFilter);
  const byName = new Map<string, CharterOrgGroup>();
  for (const u of inDistrict) {
    const name = u.charterOrg?.trim();
    if (!name) continue;
    let g = byName.get(name);
    if (!g) {
      g = {
        name,
        orgType: u.charterOrgDetail?.orgType ?? null,
        address: u.charterOrgDetail?.address ?? null,
        cityState: u.charterOrgDetail?.cityState ?? null,
        zip: u.charterOrgDetail?.zip ?? null,
        phone: u.charterOrgDetail?.phone ?? null,
        districtIds: [],
        units: [],
      };
      byName.set(name, g);
    }
    if (!g.districtIds.includes(u.districtId)) g.districtIds.push(u.districtId);
    g.units.push(u);
  }
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}

function CharterOrgsView({
  units,
  districtFilter,
  settings,
  onOpenUnit,
}: {
  units: Unit[];
  districtFilter: string;
  settings: { districts: NamedRecord[] };
  onOpenUnit: (u: Unit) => void;
}) {
  const [q, setQ] = useState('');
  const [orgType, setOrgType] = useState<string>('all');
  const [open, setOpen] = useState<CharterOrgGroup | null>(null);

  const groups = useMemo(() => groupByCharterOrg(units, districtFilter), [units, districtFilter]);

  const presentTypes = useMemo(
    () => [...new Set(groups.map((g) => g.orgType).filter((t): t is string => !!t))].sort((a, b) => a.localeCompare(b)),
    [groups],
  );

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return groups
      .filter((g) => orgType === 'all' || g.orgType === orgType)
      .filter(
        (g) => !needle || g.name.toLowerCase().includes(needle) || (g.orgType ?? '').toLowerCase().includes(needle),
      );
  }, [groups, q, orgType]);

  if (groups.length === 0) {
    return (
      <div className="card empty">
        No chartered organizations yet — import the Chartered Organizations report.
      </div>
    );
  }

  return (
    <>
      <div className="uh-leadertools">
        <input
          placeholder="Search organization name or type…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {presentTypes.length > 1 && (
          <select value={orgType} onChange={(e) => setOrgType(e.target.value)}>
            <option value="all">All types</option>
            {presentTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        )}
      </div>
      {(q.trim() || orgType !== 'all') && (
        <div className="uh-filterbar">
          Showing <b>{rows.length}</b> of {groups.length}
          {orgType !== 'all' && (
            <>
              {' — '}
              <span className="uh-filterchip">{orgType}</span>
            </>
          )}
          <button className="uh-filterclear" onClick={() => { setQ(''); setOrgType('all'); }}>
            Clear ✕
          </button>
        </div>
      )}
      <div className="card" style={{ overflowX: 'auto' }}>
        <table className="uh-table">
          <thead>
            <tr>
              <th>Organization</th>
              <th>Type</th>
              <th>Units chartered</th>
              <th>District</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((g) => (
              <tr key={g.name} className="uh-row" onClick={() => setOpen(g)}>
                <td>
                  <b>{g.name}</b>
                </td>
                <td>{g.orgType ?? '—'}</td>
                <td>{g.units.length}</td>
                <td>{g.districtIds.map((id) => recName(settings.districts, id)).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <Drawer
          title={open.name}
          subtitle={open.orgType ? <span className="subtitle">{open.orgType}</span> : undefined}
          onClose={() => setOpen(null)}
        >
          <div className="uh-drawer-block">
            <h4>Organization</h4>
            <div className="subtitle">
              {open.address ? `${open.address}, ` : ''}
              {open.cityState ?? ''} {open.zip ?? ''}
            </div>
            <div className="subtitle">{open.phone ? <PhoneLink phone={open.phone} /> : 'No phone on file'}</div>
          </div>
          <div className="uh-drawer-block">
            <h4>
              Units chartered — {open.units.length}
            </h4>
            {open.units.map((u) => {
              const rc = rechartCountdown(u);
              return (
                <div
                  key={u.id}
                  style={{ padding: '5px 0', borderBottom: '1px solid var(--line)', cursor: 'pointer' }}
                  onClick={() => onOpenUnit(u)}
                >
                  <div>
                    <b>{u.name}</b>{' '}
                    <span className="subtitle" style={{ textTransform: 'capitalize' }}>
                      {u.type} · {recName(settings.districts, u.districtId)}
                    </span>
                  </div>
                  {u.recharter && (
                    <div className="subtitle">
                      {rc && <ToneDot tone={rc.tone} />} Charter expires {u.recharter.charterExpires}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Drawer>
      )}
    </>
  );
}

// ---- Leadership Attention (council/district Leadership Sustainability view) ----
// Answers "which units deserve me looking more closely at their leadership
// situation" — deliberately factual categories, not a ranked leaderboard. A
// unit can appear in multiple categories at once. See
// attentionCategoriesFor() / ATTENTION_CATEGORY_LABELS in
// lib/leadershipSustainability.ts for the category definitions.

const ATTENTION_CATEGORY_ORDER: AttentionCategory[] = [
  'missingKeyLeadership',
  'topLeaderSupportAttention',
  'leadershipLossObserved',
  'trainingAttention',
  'sytAttention',
  'concentratedRoles',
  'insufficientData',
];

function AttentionView({
  profile,
  units,
  leadersByUnit,
  sytByMember,
  sytLoading,
  trainedLeadersImportExists,
  districtFilter,
  settings,
  onOpenUnit,
}: {
  profile: UserProfile;
  units: Unit[];
  leadersByUnit: Map<string, UnitLeader[]>;
  sytByMember: Map<string, UnitSyt>;
  sytLoading: boolean;
  trainedLeadersImportExists: boolean;
  districtFilter: string;
  settings: { districts: NamedRecord[] };
  onOpenUnit: (u: Unit) => void;
}) {
  const [categories, setCategories] = useState<Set<AttentionCategory>>(() => new Set());
  const [showExplainer, setShowExplainer] = useState(false);
  const recentSnaps = useRecentUnitLeaderSnapshots(profile);

  const snapshotsByUnit = useMemo(() => {
    const map = new Map<string, UnitLeaderSnapshot[]>();
    for (const date of recentSnaps.dates) {
      for (const s of recentSnaps.byDate.get(date) ?? []) {
        if (!s.unitId) continue;
        const arr = map.get(s.unitId) ?? [];
        arr.push(s);
        map.set(s.unitId, arr);
      }
    }
    return map;
  }, [recentSnaps.dates, recentSnaps.byDate]);

  const results = useMemo(
    () =>
      units.map((unit) =>
        computeUnitLeadershipSustainability({
          unit,
          unitLeaders: leadersByUnit.get(unit.id) ?? [],
          sytByMember,
          trainedLeadersImportExists,
          snapshots: snapshotsByUnit.get(unit.id) ?? [],
        }),
      ),
    [units, leadersByUnit, sytByMember, trainedLeadersImportExists, snapshotsByUnit],
  );

  const byCategory = useMemo(() => {
    const map = new Map<AttentionCategory, number>();
    for (const cat of ATTENTION_CATEGORY_ORDER) map.set(cat, 0);
    for (const r of results) {
      for (const cat of attentionCategoriesFor(r)) map.set(cat, (map.get(cat) ?? 0) + 1);
    }
    return map;
  }, [results]);

  const toggleCategory = (c: AttentionCategory) =>
    setCategories((cur) => {
      const next = new Set(cur);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  const rows = useMemo(() => {
    return units
      .map((unit, i) => ({ unit, result: results[i], cats: attentionCategoriesFor(results[i]) }))
      .filter((r) => categories.size === 0 || r.cats.some((c) => categories.has(c)))
      .filter((r) => r.cats.length > 0)
      .sort((a, b) => a.unit.name.localeCompare(b.unit.name));
  }, [units, results, categories]);

  if ((recentSnaps.loading && units.length === 0) || sytLoading) {
    return <div className="empty"><Spinner label="Loading…" /></div>;
  }

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: '4px 12px', marginTop: 0 }}>
        <p className="subtitle" style={{ margin: 0 }}>
          Units with at least one leadership fact worth a closer look — not a ranking. A unit can appear
          in more than one category below.
        </p>
        <button type="button" className="uh-action-open-link" style={{ whiteSpace: 'nowrap' }} onClick={() => setShowExplainer(true)}>
          ⓘ How Attention works
        </button>
      </div>
      {showExplainer && <AttentionExplainerModal onClose={() => setShowExplainer(false)} />}
      <div className="uh-leaderchips" style={{ marginBottom: 10 }}>
        {ATTENTION_CATEGORY_ORDER.map((c) => (
          <button
            key={c}
            type="button"
            className={`uh-chip${categories.has(c) ? ' is-on' : ''}`}
            aria-pressed={categories.has(c)}
            onClick={() => toggleCategory(c)}
          >
            {ATTENTION_CATEGORY_LABELS[c]} ({byCategory.get(c) ?? 0})
          </button>
        ))}
        {categories.size > 0 && (
          <button className="uh-filterclear" onClick={() => setCategories(new Set())}>
            Clear ✕
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="card empty">
          {categories.size > 0
            ? 'No units match the selected categories.'
            : 'Nothing here deserves a closer look right now, based on current data.'}
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="uh-table">
            <thead>
              <tr>
                <th>Unit</th>
                {districtFilter === 'All' && <th>District</th>}
                <th>Categories</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ unit, cats }) => (
                <tr key={unit.id} className="uh-row" onClick={() => onOpenUnit(unit)}>
                  <td>
                    <b>{unit.name}</b>
                  </td>
                  {districtFilter === 'All' && <td>{recName(settings.districts, unit.districtId)}</td>}
                  <td>
                    {cats.map((c) => (
                      <span key={c} className="uh-filterchip" style={{ marginRight: 6 }}>
                        {ATTENTION_CATEGORY_LABELS[c]}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// One centralized explainer for every Attention category, rather than an
// info icon per chip — content is verified directly against
// computeAttentionSignals()/attentionCategoriesFor() in
// lib/leadershipSustainability.ts (see docs/product-module-architecture.md
// §17.14) and must be kept in sync with that file, not the other way
// around: this describes the software, it never redefines it.
function AttentionExplainerModal({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="How Leadership Attention Works" onClose={onClose}>
      <p className="subtitle" style={{ marginTop: 10 }}>
        Compass does not score or rank units. Attention highlights specific, factual leadership
        signals that may deserve a closer look, based on the most recently imported data. A unit
        can appear in more than one category below, or in none.
      </p>
      <div className="uh-sustain-facts" style={{ marginTop: 14 }}>
        <div>
          <b>Missing Key Leadership</b>
          <div className="subtitle">
            One of a unit's three Key 3 seats — its top leader (Cubmaster/Scoutmaster), Committee
            Chair, or Chartered Organization Representative — isn't currently identified in
            Compass's Key 3 data. One item is shown per missing seat.
          </div>
        </div>
        <div>
          <b>Top-Leader Support Attention</b>
          <div className="subtitle">
            For Troops: a Scoutmaster is registered, but no Assistant Scoutmaster is currently
            registered. This does not mean the unit lacks informal leadership support or a
            succession plan — Compass can only see what's registered. (A Pack's Assistant
            Cubmaster status is shown as factual Program Leadership information instead, not as an
            Attention item, since most Packs don't register one.)
          </div>
        </div>
        <div>
          <b>Leadership Loss Observed</b>
          <div className="subtitle">
            Where Compass has at least two recorded leadership snapshots for a unit, this flags any
            decrease since the most recent prior snapshot — a previously-registered leader no
            longer appearing, or fewer Den Leaders (Packs) or Assistant Scoutmasters/Cubmasters
            than before. Most units don't have enough snapshot history yet for this to apply.
          </div>
        </div>
        <div>
          <b>Training Attention</b>
          <div className="subtitle">
            None of a unit's currently-registered program-facing leaders (its top leader, assistant
            leaders, and — for Packs — Den Leaders) are recorded as fully trained. A leader whose
            training status isn't known, or a unit without enough leadership data on file, is never
            counted as untrained — this only appears when the answer is reliably known.
          </div>
        </div>
        <div>
          <b>SYT Attention</b>
          <div className="subtitle">
            At least one of a unit's registered leaders has a Safeguarding Youth Training record
            that is confirmed not current (expired or incomplete). A leader with no SYT record on
            file at all is treated separately, never automatically counted as not current.
          </div>
        </div>
        <div>
          <b>Concentrated Roles</b>
          <div className="subtitle">
            More than half of a unit's registered leaders hold more than one leadership position at
            once. This describes how spread out — or concentrated — responsibility currently is
            across the team; it does not mean the unit is understaffed.
          </div>
        </div>
        <div>
          <b>Insufficient Data</b>
          <div className="subtitle">
            Compass doesn't yet have a Trained Leaders import or a Key 3 report on file for this
            unit, so some of these leadership facts can't be shown reliably yet. This is a data
            gap, not a finding about the unit.
          </div>
        </div>
      </div>
      <p className="subtitle" style={{ marginTop: 14 }}>
        These are factual signals about what's currently on file — not a determination that a unit
        is healthy, weak, or at risk. Local leadership context always matters more than any single
        item here.
      </p>
    </Modal>
  );
}

function LeadersView({
  leaders,
  sytByMember,
  sytLoading,
  districtFilter,
  settings,
  filter,
  onFilter,
  onRows,
}: {
  leaders: UnitLeader[];
  sytByMember: Map<string, UnitSyt>;
  sytLoading: boolean;
  districtFilter: string;
  settings: { districts: { id: string; name: string }[] };
  filter: LeaderFilter;
  onFilter: (f: LeaderFilter) => void;
  onRows?: (rows: MergedLeader[]) => void;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<MergedLeader | null>(null);
  const [page, setPage] = useState(1);

  const merged = useMemo(
    () => mergeLeaders(leaders, settings.districts),
    [leaders, settings.districts],
  );

  const inDistrict = useMemo(
    () => merged.filter((m) => districtFilter === 'All' || m.districtIds.includes(districtFilter)),
    [merged, districtFilter],
  );

  const matchesFilter = (m: MergedLeader) => leaderMatchesFilter(m, filter, sytByMember);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return inDistrict
      .filter(matchesFilter)
      .filter(
        (m) =>
          !needle ||
          m.fullName.toLowerCase().includes(needle) ||
          m.email.toLowerCase().includes(needle) ||
          m.roles.some((r) => r.position.toLowerCase().includes(needle)) ||
          m.places.some((p) => p.toLowerCase().includes(needle)),
      )
      .sort(
        (a, b) =>
          SCOPE_RANK[a.scopes[0]] - SCOPE_RANK[b.scopes[0]] ||
          a.lastName.localeCompare(b.lastName),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inDistrict, q, filter, sytByMember]);

  useEffect(() => {
    onRows?.(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // Pagination is display-only — `rows` above (what's searched/filtered/
  // exported) is never sliced. Only which page is shown resets here.
  useEffect(() => {
    setPage(1);
  }, [q, filter, districtFilter]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageClamped = Math.min(page, pageCount);
  const pageRows = useMemo(
    () => rows.slice((pageClamped - 1) * PAGE_SIZE, pageClamped * PAGE_SIZE),
    [rows, pageClamped],
  );

  if (leaders.length === 0) {
    return (
      <div className="card empty">
        No leaders yet — import the Trained Leaders Status report.
      </div>
    );
  }

  return (
    <>
      <div className="uh-leadertools">
        <input
          placeholder="Search name, email, position, unit…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="uh-leaderchips">
          {(
            ['keyThree', 'untrained', 'sytNotCurrent', 'hazardousWeather', 'councilExecBoard'] as const
          ).map((f) => (
            <button
              key={f}
              type="button"
              className={`uh-chip${filter === f ? ' is-on' : ''}`}
              aria-pressed={filter === f}
              onClick={() => onFilter(filter === f ? 'all' : f)}
            >
              {LEADER_FILTER_LABELS[f]}
            </button>
          ))}
        </div>
      </div>
      {filter !== 'all' && (
        <div className="uh-filterbar">
          Showing <b>{rows.length}</b> of {inDistrict.length} —{' '}
          <span className="uh-filterchip">{LEADER_FILTER_LABELS[filter]}</span>
          <button className="uh-filterclear" onClick={() => onFilter('all')}>
            Clear ✕
          </button>
        </div>
      )}
      <div className="card" style={{ overflowX: 'auto' }}>
        <table className="uh-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Scope</th>
              <th>Where</th>
              <th>Position(s)</th>
              <th>
                Trained{' '}
                <InfoTip label="What Trained means" width={280}>
                  Trained status reflects whether a leader has completed the training required
                  for their registered position. Incomplete means one or more required courses
                  are still owed. Some required courses, including Hazardous Weather for
                  applicable leaders, are time-limited and can expire.
                </InfoTip>
              </th>
              <th>SYT</th>
              <th>Weather</th>
              <th>Renews in</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((m) => {
              const ss = sytState(sytByMember.get(m.memberId));
              const hw = evaluateHazardousWeather(m.roles);
              return (
                <tr key={m.memberId} className="uh-row" onClick={() => setOpen(m)}>
                  <td>
                    <b>{m.fullName}</b>
                    {m.email && <div className="subtitle"><EmailLink email={m.email} /></div>}
                  </td>
                  <td>{m.scopes.map((s) => LEADER_SCOPE_LABELS[s]).join(', ')}</td>
                  <td className="uh-poscell">
                    {m.places.map((p, i) => (
                      <div key={i}>{p}</div>
                    ))}
                  </td>
                  <td className="uh-poscell">
                    {m.roles.map((r, i) => (
                      <div key={i} className={r.trained ? '' : 'uh-num-red'}>
                        {r.position}
                        {r.scope === 'unit' && (
                          <span className="subtitle"> · {r.where}</span>
                        )}
                      </div>
                    ))}
                  </td>
                  <td>
                    <span className="uh-cell">
                      <ToneDot tone={m.anyUntrained ? 'red' : 'ok'} />
                      {m.anyUntrained ? 'incomplete' : 'trained'}
                    </span>
                  </td>
                  <td>
                    {sytLoading ? (
                      <span className="uh-cell">
                        <Spinner size={12} />
                        <span className="subtitle">loading…</span>
                      </span>
                    ) : ss ? (
                      <span className="uh-cell">
                        <ToneDot tone={ss.tone} />
                        {ss.label}
                      </span>
                    ) : (
                      <span className="subtitle">no SYT record</span>
                    )}
                  </td>
                  <td>
                    {(() => {
                      const hwTone = HAZARDOUS_WEATHER_TONE[hw.status];
                      return hwTone === null ? (
                        <span className="subtitle">—</span>
                      ) : (
                        <span className="uh-cell">
                          <ToneDot tone={hwTone} />
                          {HAZARDOUS_WEATHER_STATUS_LABELS[hw.status]}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    {sytLoading ? (
                      <span className="subtitle">…</span>
                    ) : ss && ss.days != null ? (
                      <span className={`uh-pill uh-${ss.tone}`}>
                        {ss.days < 0 ? 'overdue' : `${ss.days}d`}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <PaginationBar
          page={pageClamped}
          pageCount={pageCount}
          totalCount={rows.length}
          onPage={setPage}
        />
      </div>

      {open && (
        <LeaderDrawer
          leader={open}
          syt={sytByMember.get(open.memberId)}
          sytLoading={sytLoading}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

// ---- leader drawer ----------------------------------------------------

function LeaderDrawer({
  leader,
  syt,
  sytLoading,
  onClose,
}: {
  leader: MergedLeader;
  syt: UnitSyt | undefined;
  sytLoading: boolean;
  onClose: () => void;
}) {
  const ss = sytState(syt);
  const hw = evaluateHazardousWeather(leader.roles);
  const regDays =
    leader.registrationExpires != null ? daysUntil(leader.registrationExpires) : null;

  // roles grouped by where, senior scope first (already sorted in mergeLeaders)
  const groups: { where: string; roles: LeaderRole[] }[] = [];
  for (const r of leader.roles) {
    const g = groups.find((x) => x.where === r.where);
    if (g) g.roles.push(r);
    else groups.push({ where: r.where, roles: [r] });
  }

  return (
    <Drawer
      title={leader.fullName}
      subtitle={
        <span className="subtitle">
          {leader.places.join(' · ')} · member {leader.memberId}
        </span>
      }
      onClose={onClose}
    >
      <div className="uh-drawer-block">
        <h4>Contact</h4>
        <div className="subtitle">
          {leader.email ? <EmailLink email={leader.email} /> : 'no email on file'}
          {syt?.phone ? <> · <PhoneLink phone={syt.phone} /></> : ''}
        </div>
      </div>

      <div className="uh-drawer-block">
        <h4>Safeguarding Youth Training</h4>
        {sytLoading ? (
          <div className="uh-cell">
            <Spinner size={12} />
            <span className="subtitle">Loading SYT data…</span>
          </div>
        ) : ss ? (
          <>
            <div className="uh-cell">
              <ToneDot tone={ss.tone} />
              {ss.label === 'Expired'
                ? `Expired${syt!.expiresOn ? ` ${syt!.expiresOn}` : ''}`
                : `Current${syt!.expiresOn ? ` — expires ${syt!.expiresOn}` : ''}`}
              {ss.days != null && (
                <span className="subtitle">
                  {' '}
                  ({ss.days < 0 ? `${-ss.days} days ago` : `${ss.days} days`})
                </span>
              )}
            </div>
            {syt!.completedOn && (
              <div className="subtitle">Last completed {syt!.completedOn}</div>
            )}
            {syt!.modulesToDo.length > 0 && (
              <div className="uh-num-red" style={{ marginTop: 4 }}>
                Still needs: {syt!.modulesToDo.join(', ')}
              </div>
            )}
          </>
        ) : (
          <div className="subtitle">
            No SYT record for this person in the imported report.
          </div>
        )}
      </div>

      <div className="uh-drawer-block">
        <h4>Hazardous Weather Training</h4>
        {hw.status === 'not_applicable' ? (
          <div className="subtitle">
            Not required — none of this leader's current positions call for it.
          </div>
        ) : (
          <>
            <div className="uh-cell">
              {(() => {
                const hwTone = HAZARDOUS_WEATHER_TONE[hw.status];
                return hwTone !== null && <ToneDot tone={hwTone} />;
              })()}
              {hw.status === 'expired' &&
                `Expired${hw.expirationDate ? ` ${hw.expirationDate}` : ''}`}
              {hw.status === 'due_soon' &&
                `Due soon${hw.expirationDate ? ` — expires ${hw.expirationDate}` : ''}`}
              {hw.status === 'current' &&
                `Current${hw.expirationDate ? ` — expires ${hw.expirationDate}` : ''}`}
              {hw.status === 'missing' && 'Missing'}
              {hw.daysRemaining != null && (
                <span className="subtitle">
                  {' '}
                  ({hw.daysRemaining < 0
                    ? `${-hw.daysRemaining} days ago`
                    : `${hw.daysRemaining} days`})
                </span>
              )}
            </div>
            {hw.completedDate && (
              <div className="subtitle">Last completed {hw.completedDate}</div>
            )}
            <div className="subtitle" style={{ marginTop: 4 }}>
              Required for: {hw.applicablePositions.map((p) => p.position).join(', ')}
            </div>
          </>
        )}
      </div>

      <div className="uh-drawer-block">
        <h4>Positions</h4>
        {groups.map((g, gi) => (
          <div key={gi} style={{ marginBottom: 8 }}>
            <div className="subtitle" style={{ fontWeight: 600 }}>{g.where}</div>
            {g.roles.map((r, i) => (
              <div key={i} style={{ padding: '3px 0' }}>
                <div className="uh-cell">
                  <ToneDot tone={r.trained ? 'ok' : 'red'} />
                  {r.position}
                  {r.directContact && (
                    <span className="subtitle"> · direct-contact leader</span>
                  )}
                </div>
                {!r.trained && (
                  <div
                    className="uh-num-red"
                    style={{ fontSize: 12 }}
                    title={r.incompleteCourses.join(', ') || undefined}
                  >
                    {describeIncomplete(r.incompleteCourses)}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {leader.registrationExpires && (
        <div className="uh-drawer-block">
          <h4>Registration</h4>
          <div className={regDays != null && regDays < 60 ? 'uh-num-red' : 'subtitle'}>
            Expires {leader.registrationExpires}
            {regDays != null && ` (${regDays < 0 ? 'overdue' : `${regDays} days`})`}
            {leader.roles.length > 1 && (
              <span className="subtitle"> · soonest across {leader.places.length} registrations</span>
            )}
          </div>
        </div>
      )}

      <div className="subtitle" style={{ marginTop: 4 }}>
        {syt ? `SYT as of ${syt.reportDate} · ` : ''}
        leader data as of {leader.reportDate}
      </div>
    </Drawer>
  );
}

// ---- unit drawer -------------------------------------------------

function UnitDrawer({
  unit,
  districtName,
  leaders,
  commissionerLinks,
  commissionerDataAvailable,
  sytByMember,
  sytLoading,
  ensureSytLoaded,
  trainedLeadersImportExists,
  onClose,
  openRechartAction,
  onOpenAction,
}: {
  unit: Unit;
  districtName: string;
  leaders: UnitLeader[];
  commissionerLinks: UnitCommissionerAssignment[];
  commissionerDataAvailable: boolean;
  sytByMember: Map<string, UnitSyt>;
  sytLoading: boolean;
  ensureSytLoaded: () => Promise<Map<string, UnitSyt>>;
  trainedLeadersImportExists: boolean;
  onClose: () => void;
  openRechartAction?: Action | null;
  onOpenAction?: (actionId: string) => void;
}) {
  const program = advProgram(unit);
  // Current state comes off the unit doc; the history sparkline is a one-shot
  // scoped fetch that only fires while this drawer is open.
  const historyQ = useUnitAdvancementHistory(unit.id);
  const series = useMemo(
    () =>
      [...historyQ.data]
        .filter((s) => s.program === program)
        .sort((a, b) => a.reportDate.localeCompare(b.reportDate)),
    [historyQ.data, program],
  );
  const latest = advForUnit(unit);

  const trained = leaders.filter((l) => !l.anyUntrained).length;
  const sortedLeaders = [...leaders].sort(
    (a, b) =>
      Number(b.positions.some((p) => p.directContact)) -
        Number(a.positions.some((p) => p.directContact)) || a.lastName.localeCompare(b.lastName),
  );

  // Export Unit (.xlsx) — reuses this drawer's own already-loaded,
  // already-permission-scoped data (unit/leaders/sytByMember). No
  // additional Firestore reads beyond SYT, which the drawer's own
  // `sytNeeded` trigger already started fetching the moment it opened —
  // this just waits for it if it hasn't landed yet, rather than shipping
  // an export missing SYT data. Unit Export V1 deliberately only
  // reorganizes what Unit Health itself already surfaces (see
  // src/lib/reports/unitExport.ts's header for why commissioner data is
  // NOT included here even though it's separately authorized).
  const { profile, settings } = useAppContext();
  // Names only (no contact detail), through the same server lookup the assignment pickers use.
  const labelFor = usePersonLabels(commissionerLinks.map((l) => l.personId));
  const commissioners = commissionerLines(commissionerLinks, settings.positions, labelFor, { personId: unit.commissionerPersonId, districtId: unit.districtId });
  const [exportingUnit, setExportingUnit] = useState(false);
  const handleExportUnit = async () => {
    setExportingUnit(true);
    try {
      const freshSytByMember = await ensureSytLoaded();
      const result = buildUnitExport({
        unit,
        districtName,
        leaders,
        sytByMember: freshSytByMember,
        trainedLeadersImportExists,
        profile,
      });
      if (!result.ok) {
        // Not reachable via normal navigation — this drawer only ever opens
        // for a unit already inside the user's own permission-scoped
        // unitsQ.data — kept as defense-in-depth, not dead code.
        alert(result.reason);
        return;
      }
      await downloadReportWorkbook(result.sheets, result.filename);
    } finally {
      setExportingUnit(false);
    }
  };

  return (
    <Drawer
      title={unit.name}
      subtitle={
        <span className="subtitle" style={{ textTransform: 'capitalize' }}>
          {unit.type} · {districtName}
          {unit.charterOrg ? ` · ${unit.charterOrg}` : ''}
          {unit.charterOrgDetail?.orgType ? ` (${unit.charterOrgDetail.orgType})` : ''}
        </span>
      }
      onClose={onClose}
    >
      <div className="uh-drawer-block" style={{ marginBottom: 14 }}>
        <button type="button" className="btn" onClick={handleExportUnit} disabled={exportingUnit}>
          {exportingUnit ? <Spinner size={14} /> : 'Export Unit (.xlsx)'}
        </button>
      </div>

      {unit.charterOrgDetail && (
        <div className="uh-drawer-block">
          <h4>Charter Organization</h4>
          <div>
            <b>{unit.charterOrg ?? '—'}</b>
            {unit.charterOrgDetail.orgType ? ` — ${unit.charterOrgDetail.orgType}` : ''}
          </div>
          <div className="subtitle">
            {unit.charterOrgDetail.address ? `${unit.charterOrgDetail.address}, ` : ''}
            {unit.charterOrgDetail.cityState ?? ''} {unit.charterOrgDetail.zip ?? ''}
          </div>
          <div className="subtitle">{unit.charterOrgDetail.phone ? <PhoneLink phone={unit.charterOrgDetail.phone} /> : 'No phone on file'}</div>
          <div className="subtitle" style={{ marginTop: 4 }}>as of {unit.charterOrgDetail.reportDate}</div>
        </div>
      )}

      {commissionerDataAvailable && (
        <div className="uh-drawer-block">
          <h4>{commissioners.length > 1 ? 'Assigned Commissioners' : 'Assigned Commissioner'}</h4>
          {commissioners.length === 0 ? (
            <div className="subtitle">No commissioner assigned.</div>
          ) : (
            commissioners.map((c) => (
              <div key={c.personId} style={{ padding: '4px 0', borderBottom: '1px solid var(--line)' }}>
                <div>
                  <b>{c.name ?? '—'}</b>{' '}
                  <span className="subtitle">{c.operational ? '· Operational commissioner' : '· Associated'}</span>
                </div>
                <div className="subtitle">
                  {[c.positions.join(', '), recName(settings.districts, c.districtId), c.origins.map((o) => ORIGIN_LABELS[o]).join(' + ')]
                    .filter(Boolean)
                    .join(' · ')}
                  {c.positionExpiry ? ` · position expires ${c.positionExpiry}` : ''}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {unit.recharter && (
        <div className="uh-drawer-block">
          <h4>Recharter</h4>
          <div>
            Charter expires <b>{unit.recharter.charterExpires}</b> ({daysUntil(unit.recharter.charterExpires)} days)
            {openRechartAction && (
              <button
                type="button"
                className="uh-action-open-link"
                onClick={() => onOpenAction?.(openRechartAction.id)}
              >
                Action open
              </button>
            )}
          </div>
          <div className="subtitle">
            {unit.recharter.paidYouth} youth · {unit.recharter.paidAdults} adults ·{' '}
            {unit.recharter.scoutLifeYouth} Scout Life · as of {unit.recharter.reportDate}
          </div>
        </div>
      )}

      {unit.key3 && (
        <div className="uh-drawer-block">
          <h4>Key 3 &amp; Safeguarding Youth Training</h4>
          {unit.key3.people.map((p, i) => (
            <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid var(--line)' }}>
              <div>
                <b>{p.position}</b> — {p.name}{' '}
                <span className={`uh-cell`}>
                  <ToneDot tone={YPT_TONE[p.ypt] ?? 'amber'} />
                  {p.yptRaw}
                </span>
              </div>
              <div className="subtitle">
                <EmailLink email={p.email} />
                {p.phone ? <> · <PhoneLink phone={p.phone} /></> : ''}
              </div>
            </div>
          ))}
          <div className="subtitle" style={{ marginTop: 4 }}>as of {unit.key3.reportDate}</div>
        </div>
      )}

      <LeadershipSustainabilityBlock
        unit={unit}
        leaders={leaders}
        sytByMember={sytByMember}
        sytLoading={sytLoading}
        trainedLeadersImportExists={trainedLeadersImportExists}
      />

      {leaders.length > 0 && (
        <div className="uh-drawer-block">
          <h4>
            Leaders — {trained} of {leaders.length} trained
          </h4>
          {sortedLeaders.map((l) => {
            const untrainedPos = l.positions.filter((p) => !p.trained).map((p) => p.position);
            const expSoon = l.registrationExpires != null && daysUntil(l.registrationExpires) < 60;
            // Safeguarding Youth Training is a baseline requirement for every
            // registered adult leader, independent of position-specific
            // training — shown here for all of them, not only Key 3. Reads
            // the exact same sytByMember map the "X of Y current on SYT"
            // aggregate above (LeadershipSustainabilityBlock) is computed
            // from, and the same sytState() helper the Leaders tab table
            // already uses, so the two can never disagree.
            const ss = sytState(sytByMember.get(l.memberId));
            return (
              <div key={l.id} style={{ padding: '5px 0', borderBottom: '1px solid var(--line)' }}>
                <div>
                  <b>{l.fullName}</b>{' '}
                  <span className="subtitle">{l.positions.map((p) => p.position).join(', ')}</span>
                </div>
                <div className="subtitle">
                  <EmailLink email={l.email} />
                  {untrainedPos.length > 0 && (
                    <span className="uh-num-red"> · untrained: {untrainedPos.join(', ')}</span>
                  )}
                  {expSoon && (
                    <span className="uh-num-red"> · registration expires {l.registrationExpires}</span>
                  )}
                  {sytLoading ? (
                    <span> · SYT: loading…</span>
                  ) : ss ? (
                    <span className={ss.label === 'Expired' ? 'uh-num-red' : undefined}> · SYT: {ss.label}</span>
                  ) : (
                    <span className="uh-num-red"> · SYT: no record</span>
                  )}
                </div>
              </div>
            );
          })}
          <div className="subtitle" style={{ marginTop: 4 }}>
            {leaders[0]?.reportDate ? `as of ${leaders[0].reportDate}` : ''}
          </div>
        </div>
      )}

      {latest && (
        <div className="uh-drawer-block">
          <h4>Advancement — {latest.programYear} year-to-date</h4>
          <div className="subtitle">
            {latest.ranksYtd} rank advancement{latest.ranksYtd === 1 ? '' : 's'}
            {program === 'scoutsBSA'
              ? ` · ${latest.eaglesYtd} Eagle${latest.eaglesYtd === 1 ? '' : 's'} · ${latest.meritBadgesYtd} merit badge${latest.meritBadgesYtd === 1 ? '' : 's'}`
              : ''}{' '}
            in {latest.programYear} as of {latest.reportDate} · {latest.youth} youth registered
          </div>
          <div className="subtitle" style={{ marginTop: 6, fontSize: 12 }}>
            Advanced to each rank in {latest.programYear} so far (not a current roster count):
          </div>
          <div className="uh-ranks">
            {Object.entries(latest.byRank).map(([rk, v]) => (
              <span key={rk}>
                {rankLabel(rk)}: <b>{v}</b>
              </span>
            ))}
          </div>
          {series.length >= 2 && <Sparkline series={series} />}
        </div>
      )}
    </Drawer>
  );
}

// ---- Leadership Sustainability V1 -----------------------------------
// See src/lib/leadershipSustainability.ts for the evaluator this renders —
// every fact shown here comes straight from that module; this component
// adds no scoring/classification of its own. Compact by default (one line
// per section, matching the existing drawer's information density); a
// single "Show details" toggle reveals the drill-down lists so the drawer
// never opens into a wall of text.

function depthText(d: UnitLeadershipSustainability['depth']): string {
  if (d.availability !== 'available' || d.count === null) return 'Insufficient Data';
  return `${d.count} registered leader${d.count === 1 ? '' : 's'}${d.asOf ? ` · as of ${d.asOf}` : ''}`;
}

function concentrationText(c: UnitLeadershipSustainability['concentration']): string {
  if (c.availability !== 'available') return 'Insufficient Data';
  return `${c.multiRoleCount} of ${c.totalLeaders} registered leader${c.totalLeaders === 1 ? '' : 's'} hold multiple positions`;
}

// V1.1: Training and SYT are shown as two separate facts (previously one
// combined "Leadership Readiness" line) — matches the product decision to
// keep them visually distinct, never combined into one condition. Neither
// function's underlying data or SYT's attention logic changed; this only
// changes where/how they're grouped in the drawer.
function trainingText(r: UnitLeadershipSustainability['readiness']): string {
  if (r.availability !== 'available') return 'Insufficient Data';
  return `${r.trainedCount} of ${r.totalLeaders} registered leader${r.totalLeaders === 1 ? '' : 's'} trained · ${r.trainedPercent}%`;
}

function sytText(r: UnitLeadershipSustainability['readiness']): string {
  if (r.availability !== 'available') return 'Insufficient Data';
  return `${r.sytCurrentCount} of ${r.totalLeaders} current on SYT`;
}

function youthContextText(y: UnitLeadershipSustainability['youthContext']): string {
  if (y.availability !== 'available' || y.ratio === null) return 'Insufficient Data';
  return `${y.youth} youth · ${y.leaders} registered leader${y.leaders === 1 ? '' : 's'} · ${y.ratio.toFixed(1)} youth per leader`;
}

function assistantTopLeaderLabel(unitType: UnitType): string {
  return unitType === 'troop' ? 'Assistant Scoutmaster' : 'Assistant Cubmaster';
}

function LeadershipSustainabilityBlock({
  unit,
  leaders,
  sytByMember,
  sytLoading,
  trainedLeadersImportExists,
}: {
  unit: Unit;
  leaders: UnitLeader[];
  sytByMember: Map<string, UnitSyt>;
  sytLoading: boolean;
  trainedLeadersImportExists: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  // Own scoped fetch — this unit's full recorded history, only while the
  // drawer is open (same pattern as useUnitAdvancementHistory above).
  const snapshotsQ = useUnitLeaderSnapshots(unit.id);

  const result = useMemo(
    () =>
      computeUnitLeadershipSustainability({
        unit,
        unitLeaders: leaders,
        sytByMember,
        trainedLeadersImportExists,
        snapshots: snapshotsQ.data,
      }),
    [unit, leaders, sytByMember, trainedLeadersImportExists, snapshotsQ.data],
  );
  const { depth, keyLeadership, programLeadership, concentration, readiness, youthContext, history, attentionSignals } = result;
  const delta = history.availability === 'comparable' ? history.delta : null;

  if (sytLoading) {
    return (
      <div className="uh-drawer-block uh-sustain">
        <h4>Leadership Sustainability</h4>
        <div className="uh-cell">
          <Spinner size={12} />
          <span className="subtitle">Loading SYT data…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="uh-drawer-block uh-sustain">
      <h4>Leadership Sustainability</h4>

      {attentionSignals.length > 0 && (
        <div className="uh-sustain-signals">
          {attentionSignals.map((s, i) => (
            <div key={i} className="uh-sustain-signal">
              <b>{s.label}</b>
              <div className="subtitle">{s.detail}</div>
            </div>
          ))}
        </div>
      )}

      <div className="uh-sustain-facts">
        <div>
          <b>Leadership Depth</b>
          <div className="subtitle">{depthText(depth)}</div>
        </div>

        <div>
          <b>Key Leadership</b>
          {keyLeadership.availability === 'available' ? (
            <>
              {keyLeadership.seats.map((seat) => (
                <div key={seat.seatLabel} className="subtitle">
                  {seat.seatLabel} —{' '}
                  {seat.observed ? `✓${seat.personName ? ` ${seat.personName}` : ''}` : 'not observed'}
                </div>
              ))}
              {keyLeadership.asOf && <div className="subtitle">as of {keyLeadership.asOf}</div>}
            </>
          ) : (
            <div className="subtitle">Insufficient Data</div>
          )}
        </div>

        <div>
          <b>Program Leadership</b>
          <div className="subtitle">{programLeadership.summary}</div>
        </div>

        <div>
          <b>Role Concentration</b>
          <div className="subtitle">{concentrationText(concentration)}</div>
        </div>

        <div>
          <b>Training</b>
          <div className="subtitle">{trainingText(readiness)}</div>
        </div>

        <div>
          <b>SYT</b>
          <div className="subtitle">{sytText(readiness)}</div>
        </div>

        <div>
          <b>Current Structure</b>
          <div className="subtitle">{youthContextText(youthContext)}</div>
        </div>

        {history.availability !== 'none' && (
          <div>
            <b>{delta ? `Since ${delta.priorDate}` : `As of ${history.latestDate}`}</b>
            {!delta ? (
              <div className="subtitle">Only one snapshot recorded so far — not enough history yet for a trend.</div>
            ) : (
              <div className="subtitle uh-sustain-delta">
                {delta.leadersAdded.length > 0 && <span>+{delta.leadersAdded.length} leader{delta.leadersAdded.length === 1 ? '' : 's'}</span>}
                {delta.leadersDeparted.length > 0 && <span>-{delta.leadersDeparted.length} leader{delta.leadersDeparted.length === 1 ? '' : 's'}</span>}
                {!!delta.denLeaderDelta && (
                  <span>{delta.denLeaderDelta > 0 ? '+' : ''}{delta.denLeaderDelta} Den Leader{Math.abs(delta.denLeaderDelta) === 1 ? '' : 's'}</span>
                )}
                {!!delta.assistantTopLeaderDelta && (
                  <span>
                    {delta.assistantTopLeaderDelta > 0 ? '+' : ''}
                    {delta.assistantTopLeaderDelta} {assistantTopLeaderLabel(unit.type)}
                    {Math.abs(delta.assistantTopLeaderDelta) === 1 ? '' : 's'}
                  </span>
                )}
                {delta.leadersAdded.length === 0 &&
                  delta.leadersDeparted.length === 0 &&
                  !delta.denLeaderDelta &&
                  !delta.assistantTopLeaderDelta &&
                  delta.positionChanges.length === 0 && <span>No leadership changes observed.</span>}
              </div>
            )}
          </div>
        )}
      </div>

      <button type="button" className="uh-action-open-link" onClick={() => setExpanded((e) => !e)}>
        {expanded ? 'Hide details' : 'Show details'}
      </button>

      {expanded && (
        <div className="uh-sustain-detail">
          {concentration.multiRoleLeaders.length > 0 && (
            <div className="uh-sustain-detailsection">
              <div className="uh-sustain-detailtitle">Multiple positions</div>
              {concentration.multiRoleLeaders.map((l) => (
                <div key={l.memberId} className="subtitle">
                  {l.fullName} — {l.positions.join(', ')}
                </div>
              ))}
            </div>
          )}
          {readiness.availability === 'available' && readiness.untrainedLeaders.length > 0 && (
            <div className="uh-sustain-detailsection">
              <div className="uh-sustain-detailtitle">Incomplete position training</div>
              {readiness.untrainedLeaders.map((l) => (
                <div key={l.memberId} className="subtitle">
                  {l.fullName} — {l.untrainedPositions.join(', ')}
                </div>
              ))}
            </div>
          )}
          {readiness.availability === 'available' && readiness.sytFlagged.length > 0 && (
            <div className="uh-sustain-detailsection">
              <div className="uh-sustain-detailtitle">SYT attention</div>
              {readiness.sytFlagged.map((l) => (
                <div key={l.memberId} className="subtitle">
                  {l.fullName} — {l.status === 'notCurrent' ? 'not current on SYT' : 'no SYT record found'}
                </div>
              ))}
            </div>
          )}
          {delta && delta.leadersAdded.length > 0 && (
            <div className="uh-sustain-detailsection">
              <div className="uh-sustain-detailtitle">Added since {delta.priorDate}</div>
              {delta.leadersAdded.map((l) => (
                <div key={l.memberId} className="subtitle">
                  {l.fullName} — {l.positions.join(', ')}
                </div>
              ))}
            </div>
          )}
          {delta && delta.leadersDeparted.length > 0 && (
            <div className="uh-sustain-detailsection">
              <div className="uh-sustain-detailtitle">No longer observed since {delta.priorDate}</div>
              {delta.leadersDeparted.map((l) => (
                <div key={l.memberId} className="subtitle">
                  {l.fullName} — {l.positions.join(', ')}
                </div>
              ))}
            </div>
          )}
          {delta && delta.positionChanges.length > 0 && (
            <div className="uh-sustain-detailsection">
              <div className="uh-sustain-detailtitle">Position changes since {delta.priorDate}</div>
              {delta.positionChanges.map((c) => (
                <div key={c.memberId} className="subtitle">
                  {c.fullName}
                  {c.added.length > 0 ? ` +${c.added.join(', ')}` : ''}
                  {c.removed.length > 0 ? ` -${c.removed.join(', ')}` : ''}
                </div>
              ))}
            </div>
          )}
          {history.tenure.length > 0 && (
            <div className="uh-sustain-detailsection">
              <div className="uh-sustain-detailtitle">Apparent tenure</div>
              {history.tenure.map((t, i) => (
                <div key={i} className="subtitle">
                  {t.fullName} — {t.position} — {describeTenure(t)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Sparkline({ series }: { series: UnitAdvancementSnapshot[] }) {
  const w = 240;
  const h = 48;
  const max = Math.max(1, ...series.map((s) => s.ranksYtd));
  const pts = series.map((s, i) => {
    const x = series.length === 1 ? 0 : (i / (series.length - 1)) * (w - 4) + 2;
    const y = h - 4 - (s.ranksYtd / max) * (h - 8);
    return `${x},${y}`;
  });
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="uh-spark" role="img" aria-label="ranks over time">
      <polyline points={pts.join(' ')} fill="none" stroke="var(--blue)" strokeWidth="2" />
      {series.map((s, i) => {
        const [x, y] = pts[i].split(',').map(Number);
        return <circle key={s.id} cx={x} cy={y} r="2.5" fill="var(--blue)" />;
      })}
    </svg>
  );
}
